import React from "react";
import { Badge } from "react-bootstrap";
import { useNavigate } from "react-router-dom";
import { VerifiedIcon } from "../../../assets/icons";
import { getUserInitial } from "../../../utils/otherHelopers";
import CustomTable from "../../../components/table/CustomTable";

const AvatarWithFallback = ({ src, name, size = 30, fallbackInitial = "?" }) => {
    const [hasError, setHasError] = React.useState(false);

    if (src && !hasError) {
        return (
            <img
                src={typeof src === 'string' ? src : src?.url}
                alt={name}
                width={size}
                height={size}
                className="rounded-circle object-fit-cover"
                onError={() => setHasError(true)}
            />
        );
    }

    return (
        <div
            className="rounded-circle bg-light d-flex align-items-center justify-content-center fw-medium text-secondary"
            style={{ width: size, height: size, fontSize: size / 2.5 }}
        >
            {name ? getUserInitial(name) : fallbackInitial}
        </div>
    );
};

const statusColors = {
    pending: "warning",
    confirmed: "success",
    cancelled: "danger",
    failed: "dark"
};

const cleanMobileNumber = (mobileNumber) => {
    if (!mobileNumber) return "-";
    if (mobileNumber.startsWith("DELETED_")) {
        const parts = mobileNumber.split("_");
        if (parts.length >= 3) {
            return parts.slice(2).join("_");
        }
    }
    return mobileNumber;
};

const BookingTableSection = ({
    data,
    isLoading,
    isInitialLoad,
    currentPage,
    setCurrentPage,
    pageSize,
    setPageSize,
    totalItems
}) => {
    const navigate = useNavigate();

    const actionList = [
        {
            title: "View Details",
            handler: (item) => {
                navigate(`/marketplace/bookings/${item.id}`);
            }
        }
    ];

    const pagination = {
        currentPage,
        totalPages: Math.ceil((totalItems || 0) / (pageSize || 10)),
        total: totalItems || 0,
        onPageChange: (page) => setCurrentPage(page),
        pageSize: pageSize || 10,
        setPageSize,
        setCurrentPage,
        perPageOptions: [10, 20, 50, 100],
    };

    const columns = [
        {
            id: "buyer",
            header: "BUYER",
            cell: (_, item) => {
                const buyer = item?.user || {};
                const buyerProfilePhoto = buyer?.profile?.profile_photo?.url;
                const isBuyerDeleted = buyer?.deleted_at || (buyer?.mobile_number && buyer.mobile_number.startsWith("DELETED_"));

                return (
                    <div className="d-flex align-items-center gap-2">
                        <div style={isBuyerDeleted ? { opacity: 0.5 } : {}}>
                            <AvatarWithFallback
                                src={buyerProfilePhoto}
                                name={`${buyer?.first_name || ""} ${buyer?.last_name || ""}`}
                                size={40}
                            />
                        </div>
                        <div className="d-flex flex-column text-start">
                            <div className="d-flex align-items-center gap-1">
                                <span className={isBuyerDeleted ? "text-secondary fst-italic" : "fw-medium text-nowrap"}>
                                    {buyer?.first_name ? `${buyer.first_name} ${buyer?.last_name || ""}` : "-"}
                                </span>
                                {isBuyerDeleted && (
                                    <Badge bg="danger" className="text-capitalize px-2 py-1 rounded-1 ms-1" style={{ fontSize: "10px", lineHeight: "1" }}>
                                        Deleted
                                    </Badge>
                                )}
                                {!isBuyerDeleted && buyer?.identity_verified && (
                                    <VerifiedIcon size={14} className="text-success" />
                                )}
                            </div>
                            <span className="text-secondary small">
                                {cleanMobileNumber(buyer?.mobile_number)}
                            </span>
                        </div>
                    </div>
                );
            }
        },
        {
            id: "seller",
            header: "SELLER",
            cell: (_, item) => {
                const listing = item?.listing || {};
                const sellerType = listing?.seller_type;
                const sellerCompany = listing?.company;
                const sellerUser = listing?.user;

                let sellerName = "-";
                let sellerMobile = "-";
                let sellerLogo = null;
                let isSellerVerified = false;
                let isSellerDeleted = false;

                if (sellerType === 'company' && sellerCompany) {
                    sellerName = sellerCompany.name;
                    sellerMobile = sellerCompany.profile?.mobile_number || "-";
                    sellerLogo = sellerCompany.profile?.logo?.url;
                    isSellerVerified = sellerCompany.is_verified;
                } else if (sellerUser) {
                    sellerName = `${sellerUser.first_name || ""} ${sellerUser.last_name || ""}`.trim();
                    sellerMobile = sellerUser.mobile_number || "-";
                    sellerLogo = sellerUser.profile?.profile_photo?.url;
                    isSellerVerified = sellerUser.identity_verified;
                    isSellerDeleted = sellerUser.deleted_at || (sellerUser.mobile_number && sellerUser.mobile_number.startsWith("DELETED_"));
                }

                return (
                    <div className="d-flex align-items-center gap-2">
                        <div style={isSellerDeleted ? { opacity: 0.5 } : {}}>
                            <AvatarWithFallback
                                src={sellerLogo}
                                name={sellerName || "Seller"}
                                size={40}
                            />
                        </div>
                        <div className="d-flex flex-column text-start">
                            <div className="d-flex align-items-center gap-1">
                                <span className={isSellerDeleted ? "text-secondary fst-italic" : "fw-medium text-nowrap"}>
                                    {sellerName || "-"}
                                </span>
                                {isSellerDeleted && (
                                    <Badge bg="danger" className="text-capitalize px-2 py-1 rounded-1 ms-1" style={{ fontSize: "10px", lineHeight: "1" }}>
                                        Deleted
                                    </Badge>
                                )}
                                {!isSellerDeleted && isSellerVerified && (
                                    <VerifiedIcon size={14} className="text-success" />
                                )}
                            </div>
                            <span className="text-secondary small">
                                {cleanMobileNumber(sellerMobile)}
                            </span>
                        </div>
                    </div>
                );
            }
        },
        {
            id: "listing",
            header: "LISTING",
            cell: (_, item) => {
                const listing = item?.listing || {};
                const isListingDeleted = !!listing?.deleted_at;
                return (
                    <div className="d-flex flex-column text-start">
                        <div className="d-flex align-items-center gap-1">
                            <span className={`fw-medium text-nowrap text-truncate ${isListingDeleted ? "text-secondary fst-italic" : ""}`} style={{ maxWidth: 150 }} title={listing?.name}>
                                {listing?.name || "-"}
                            </span>
                            {isListingDeleted && (
                                <Badge bg="danger" className="text-capitalize px-2 py-1 rounded-1" style={{ fontSize: "10px", lineHeight: "1" }}>
                                    Deleted
                                </Badge>
                            )}
                        </div>
                        <span className="text-secondary small">{listing?.category?.name || "-"}</span>
                    </div>
                );
            }
        },
        {
            id: "delivery",
            header: "DELIVERY",
            cell: (_, item) => (
                <div className="d-flex flex-column text-start" style={{ minWidth: '150px' }}>
                    <span className="text-wrap small fw-medium">{item?.delivery_address || "-"}</span>
                    <span className="text-secondary small">{item?.city?.name || "-"}</span>
                </div>
            )
        },
        {
            id: "quantity",
            header: "QUANTITY",
            cell: (_, item) => (
                <span className="fw-medium text-nowrap">{item?.lots_booked || 0} Lots ({item?.total_quantity || 0} qty)</span>
            )
        },
        {
            id: "amount",
            header: "AMOUNT",
            cell: (_, item) => (
                <span className="text-secondary small fw-bold">₹ {parseFloat(item?.total_amount || 0).toLocaleString()}</span>
            )
        },
        {
            id: "booking_status",
            header: "BOOKING STATUS",
            cell: (_, item) => {
                const bookingStatus = item?.booking_status || "pending";
                const bookingBadge = statusColors[bookingStatus.toLowerCase()] || "secondary";
                return (
                    <Badge bg={bookingBadge} className="text-capitalize px-3 py-2 rounded-pill">
                        {bookingStatus}
                    </Badge>
                );
            }
        },
        {
            id: "payment_status",
            header: "PAYMENT STATUS",
            cell: (_, item) => {
                const payment = item?.payment || {};
                const paymentStatus = payment?.payment_status || "pending";
                const paymentBadge = statusColors[paymentStatus.toLowerCase()] || "secondary";
                return (
                    <Badge bg={paymentBadge} className="text-capitalize px-3 py-2 rounded-pill">
                        {paymentStatus}
                    </Badge>
                );
            }
        },
        {
            id: "payment_id",
            header: "PAYMENT ID",
            cell: (_, item) => {
                const payment = item?.payment || {};
                return (
                    <span className="small text-nowrap fw-medium text-primary">{payment?.cf_payment_id || "-"}</span>
                );
            }
        },
        {
            id: "method",
            header: "METHOD",
            cell: (_, item) => {
                const payment = item?.payment || {};
                return (
                    <span className="small text-nowrap">{payment?.payment_group || "-"}</span>
                );
            }
        },
        {
            id: "service_charge",
            header: "SERVICE CHARGE",
            cell: (_, item) => {
                const payment = item?.payment || {};
                return (
                    <span className="small text-nowrap">₹ {parseFloat(payment?.service_charge || 0).toLocaleString()}</span>
                );
            }
        },
        {
            id: "service_tax",
            header: "SERVICE TAX",
            cell: (_, item) => {
                const payment = item?.payment || {};
                return (
                    <span className="small text-nowrap">₹ {parseFloat(payment?.service_tax || 0).toLocaleString()}</span>
                );
            }
        },
        {
            id: "settlement",
            header: "SETTLEMENT",
            cell: (_, item) => {
                const payment = item?.payment || {};
                return (
                    <span className="small text-nowrap fw-bold text-success">₹ {parseFloat(payment?.settlement_amount || 0).toLocaleString()}</span>
                );
            }
        },
        {
            id: "created_at",
            header: "DATE",
            cell: (_, item) => (
                <span className="small text-nowrap">
                    {item?.created_at ? new Date(item.created_at).toLocaleString("en-IN", { timeZone: "Asia/Kolkata" }) : "-"}
                </span>
            )
        }
    ];

    return (
        <CustomTable
            data={data || []}
            columns={columns}
            loading={isLoading && !isInitialLoad}
            emptyMessage="No bookings found"
            keyField="id"
            pagination={pagination}
            actionList={actionList}
            rowClick={(row) =>
                navigate(`/marketplace/bookings/${row.id}`)
            }
        />
    );
};

export default BookingTableSection;
